import React, { useMemo } from 'react'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import {
  selectCrossClientCurrentTabLoaded,
  selectCrossClientPageMeta,
  selectCrossClientPageResults,
  selectCrossClientPaging,
  selectCrossClientResultsExecution,
  selectCrossClientSelectedResultIds,
  selectCrossClientSelectedTab,
  setCrossClientPage,
  setCrossClientPageSize,
} from '../../../store/crossClient/crossClientResults'
import { ZocksDataTable } from '../../../components/DataTable/ZocksDataTable'
import { Spinner } from '../../../components/Spinner/Spinner'
import { ConditionalStyles, TableColumn, TableStyles } from 'react-data-table-component'
import { CrossClientQueryResult } from '../../../models/CrossClient'
import { CrossClientQueryResultMatchCell } from './Cells/CrossClientQueryResultMatchCell'
import { CrossClientQueryResultSelectCell } from './Cells/CrossClientQueryResultSelectCell'
import { CrossClientResultSelectAllHeader } from './Cells/CrossClientResultSelectAllHeader'
import { CrossClientSelectionBanner } from '../Results/CrossClientSelectionBanner'
import { CrossClientQueryResultClientCell } from './Cells/CrossClientQueryResultClientCell'
import { CrossClientQueryResultOwnerCell } from './Cells/CrossClientQueryResultOwnerCell'
import { CrossClientQueryResultActionsCell } from './Cells/CrossClientQueryResultActionsCell'
import { CrossClientResultDetailsModal } from './Details/CrossClientResultDetailsModal'
import { useCrossClientQueryResultDetails } from './Details/hooks'
import { CrossClientResultButtons } from './Details/CrossClientResultButtons'
import { CrossClientTableEmptyState } from './CrossClientTableEmptyState'
import './CrossClientTable.scss'

const TABLE_STYLES: TableStyles = {
  table: {
    style: {
      border: '1px solid var(--zocks-gray-200)',
      borderRadius: '1rem',
    },
  },
  headRow: {
    style: {
      background: 'var(--zocks-gray-200)',
      border: '1px solid var(--zocks-gray-200)',
      borderRadius: '1rem 1rem 0 0',
    },
  },
  rows: {
    style: {
      animation: 'row-in 0.4s ease-out forwards',
      '&:hover': {
        background: 'var(--zocks-gray-100) !important',
      },
      '&:not(:last-of-type)': {
        borderBottomColor: 'var(--zocks-gray-200) !important',
      },
    },
  },
  noData: {
    style: {
      borderRadius: '1rem',
    },
  },
}

const SELECTION_TABLE_STYLES: TableStyles = {
  ...TABLE_STYLES,
  table: {
    style: {
      ...TABLE_STYLES.table?.style,
      borderTop: 'none',
      borderTopLeftRadius: 0,
      borderTopRightRadius: 0,
    },
  },
}

const SELECTED_ROW_BACKGROUND = 'rgba(235, 229, 252, 0.40)'

const TABLE_LOADER = (
  <div className='d-flex flex-column align-items-center justify-content-center gap-3 py-5 w-100'>
    <Spinner />
    <div className='paragraph-medium medium'>Pulling up your results…</div>
  </div>
)

export const CrossClientTable = () => {
  const dispatch = useAppDispatch()

  const execution = useAppSelector(selectCrossClientResultsExecution)
  const results = useAppSelector(selectCrossClientPageResults)
  const pageMeta = useAppSelector(selectCrossClientPageMeta)
  const paging = useAppSelector(selectCrossClientPaging)
  const tabLoaded = useAppSelector(selectCrossClientCurrentTabLoaded)
  const selectedTab = useAppSelector(selectCrossClientSelectedTab)
  const selectedResultIds = useAppSelector(selectCrossClientSelectedResultIds)

  const selectedResultDetails = useCrossClientQueryResultDetails()
  const { handleShowResultDetails } = selectedResultDetails

  const { decomposition } = execution ?? {}

  const conditionalRowStyles = useMemo<ConditionalStyles<CrossClientQueryResult>[]>(() => {
    const selectedSet = new Set(selectedResultIds)
    return [
      {
        when: (row) => selectedSet.has(row.id),
        style: {
          background: SELECTED_ROW_BACKGROUND,
          '&:hover': {
            background: `${SELECTED_ROW_BACKGROUND} !important`,
          },
        },
      },
    ]
  }, [selectedResultIds])

  if (!decomposition) {
    return <div>Unable to determine decomposition for this query</div>
  }

  const columns: TableColumn<CrossClientQueryResult>[] = [
    {
      name: <CrossClientResultSelectAllHeader />,
      width: '3.25rem',
      center: true,
      ignoreRowClick: true,
      cell: (row) => <CrossClientQueryResultSelectCell result={row} />,
    },
    {
      selector: (row) => row.clientId,
      name: 'Client',
      grow: 21,
      cell: (row) => <CrossClientQueryResultClientCell result={row} />,
    },
    {
      name: 'Owner',
      grow: 16,
      cell: (row) => <CrossClientQueryResultOwnerCell result={row} />,
    },
    {
      name: 'Match',
      grow: 5.5,
      cell: (row) => <CrossClientQueryResultMatchCell result={row} onShowDetails={() => handleShowResultDetails(row)} />,
    },
    {
      name: 'Actions',
      grow: 5.5,
      cell: (row) => <CrossClientQueryResultActionsCell result={row} />,
    },
    {
      ignoreRowClick: true,
      grow: 6,
      center: true,
      cell: (row) => <CrossClientResultButtons result={row} />,
    },
  ]

  const handlePageChanged = (page: number) => {
    dispatch(setCrossClientPage(page))
  }

  const handlePageSizeChanged = (currentRowsPerPage: number, page: number) => {
    dispatch(setCrossClientPageSize(currentRowsPerPage))
    dispatch(setCrossClientPage(page))
  }

  const hasSelection = selectedResultIds.length > 0

  return (
    <>
      <CrossClientResultDetailsModal resultDetails={selectedResultDetails} decomposition={decomposition} />
      <div className='cross-client-table-area'>
        {hasSelection ? <CrossClientSelectionBanner /> : null}
        <ZocksDataTable
          key={selectedTab}
          columns={columns}
          data={results}
          customStyles={hasSelection ? SELECTION_TABLE_STYLES : TABLE_STYLES}
          conditionalRowStyles={conditionalRowStyles}
          pagination
          paginationServer
          paginationTotalRows={pageMeta.totalElements}
          paginationDefaultPage={paging.page}
          paginationPerPage={paging.size}
          progressPending={!tabLoaded}
          progressComponent={TABLE_LOADER}
          highlightOnHover
          noTableHead={hasSelection}
          className='cross-client-table'
          onChangePage={handlePageChanged}
          onChangeRowsPerPage={handlePageSizeChanged}
          noDataComponent={<CrossClientTableEmptyState />}
        />
      </div>
    </>
  )
}
